import { useCallback, useEffect, useRef, useState } from 'react'
import { Dayjs } from 'dayjs'
import { Toast } from '@/components/Toast'
import studentApi from '@/pages/people/student/api'
import { Student } from '@/pages/people/student/types'
import userApi from '@/pages/system/user/api'
import { User, UserStatus } from '@/pages/system/user/types'
import { ID } from '@/types/common'
import classRecordApi from '../api'
import {
  ClassRecordRules,
  ClassMonthlySummary,
  ClassRecord,
  MonthPlanResult,
  StudentClassCurrentConfig,
  StudentClassGenerationBatch,
  StudentClassConfigVersion,
} from '../types'

type UseClassRecordDataOptions = {
  selectedStudentId: ID | undefined
  monthText: string
  selectedDateRange: [Dayjs, Dayjs] | null
  activeTab: 'query' | 'versions'
}

export function useClassRecordData({
  selectedStudentId,
  monthText,
  selectedDateRange,
  activeTab,
}: UseClassRecordDataOptions) {
  const [students, setStudents] = useState<Student[]>([])
  const [teachers, setTeachers] = useState<User[]>([])
  const [studentsLoading, setStudentsLoading] = useState(false)
  const [teachersLoading, setTeachersLoading] = useState(false)

  const [records, setRecords] = useState<ClassRecord[]>([])
  const [summary, setSummary] = useState<ClassMonthlySummary | null>(null)
  const [rules, setRules] = useState<ClassRecordRules | null>(null)
  const [currentConfig, setCurrentConfig] =
    useState<StudentClassCurrentConfig | null>(null)
  const [generationBatches, setGenerationBatches] = useState<StudentClassGenerationBatch[]>([])
  const [configVersions, setConfigVersions] = useState<StudentClassConfigVersion[]>([])

  const [loading, setLoading] = useState(false)
  const [hasQueried, setHasQueried] = useState(false)
  const [recordsReloadVersion, setRecordsReloadVersion] = useState(0)
  const [monthPlan, setMonthPlan] = useState<MonthPlanResult | null>(null)
  const loadStudentConfigRequestRef = useRef(0)
  const loadRecordsRequestRef = useRef(0)

  const loadStudentConfig = useCallback(async (studentId: ID) => {
    const requestId = loadStudentConfigRequestRef.current + 1
    loadStudentConfigRequestRef.current = requestId
    try {
      const [current, batches, versions] = await Promise.all([
        classRecordApi.getCurrentConfig({ studentId }),
        classRecordApi.getGenerationBatches({ studentId }),
        classRecordApi.getConfigVersions({ studentId }),
      ])
      if (requestId !== loadStudentConfigRequestRef.current) {
        return
      }

      setCurrentConfig(current)
      setGenerationBatches(batches || [])
      setConfigVersions(versions || [])
    } catch (error) {
      if (requestId !== loadStudentConfigRequestRef.current) {
        return
      }
      Toast.error('加载学生上课配置失败')
      console.error('加载学生上课配置失败:', error)
    }
  }, [])

  const loadRecords = useCallback(async () => {
    if (!selectedStudentId || !monthText) {
      return
    }
    const requestId = loadRecordsRequestRef.current + 1
    loadRecordsRequestRef.current = requestId

    try {
      setHasQueried(true)
      setLoading(true)
      setMonthPlan(null)
      const [listRes, summaryRes] = await Promise.all([
        classRecordApi.getList({
          studentId: selectedStudentId,
          month: monthText,
          startDate: selectedDateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: selectedDateRange?.[1]?.format('YYYY-MM-DD'),
        }),
        classRecordApi.getSummary({
          studentId: selectedStudentId,
          month: monthText,
        }),
      ])
      if (requestId !== loadRecordsRequestRef.current) {
        return
      }

      setRecords(listRes.data || [])
      setSummary(summaryRes)
      try {
        const monthPlanRes = await classRecordApi.getMonthPlan(selectedStudentId, monthText)
        if (requestId !== loadRecordsRequestRef.current) {
          return
        }
        setMonthPlan(monthPlanRes)
      } catch (error) {
        if (requestId !== loadRecordsRequestRef.current) {
          return
        }
        Toast.error('加载当月计划失败')
        console.error('加载当月计划失败:', error)
        setMonthPlan(null)
      }
      setRecordsReloadVersion((prev) => prev + 1)
    } catch (error) {
      if (requestId !== loadRecordsRequestRef.current) {
        return
      }
      Toast.error('加载上课记录失败')
      console.error('加载上课记录失败:', error)
    } finally {
      if (requestId === loadRecordsRequestRef.current) {
        setLoading(false)
      }
    }
  }, [
    monthText,
    selectedDateRange,
    selectedStudentId,
  ])

  useEffect(() => {
    const loadBaseData = async () => {
      try {
        setStudentsLoading(true)
        setTeachersLoading(true)
        const [studentRes, teacherRes, rulesRes] = await Promise.all([
          studentApi.getList({
            pageSize: 100,
          }),
          userApi.getList({
            roleCode: 'TEACHER',
            status: UserStatus.ACTIVE,
            pageSize: 100,
          }),
          classRecordApi.getRules(),
        ])

        setStudents(studentRes.data || [])
        setTeachers(teacherRes.data || [])
        setRules(rulesRes)
      } catch (error) {
        Toast.error('加载基础数据失败')
        console.error('加载基础数据失败:', error)
      } finally {
        setStudentsLoading(false)
        setTeachersLoading(false)
      }
    }

    loadBaseData()
  }, [])

  useEffect(() => {
    if (!selectedStudentId) {
      loadStudentConfigRequestRef.current += 1
      setCurrentConfig(null)
      setGenerationBatches([])
      setConfigVersions([])
      return
    }

    loadStudentConfig(selectedStudentId)
  }, [loadStudentConfig, selectedStudentId])

  useEffect(() => {
    if (activeTab !== 'query') {
      return
    }
    if (!selectedStudentId || !monthText) {
      loadRecordsRequestRef.current += 1
      return
    }
    loadRecords()
  }, [activeTab, loadRecords, monthText, selectedDateRange, selectedStudentId])

  useEffect(
    () => () => {
      loadStudentConfigRequestRef.current += 1
      loadRecordsRequestRef.current += 1
    },
    [],
  )

  return {
    students,
    teachers,
    studentsLoading,
    teachersLoading,
    records,
    setRecords,
    summary,
    rules,
    setRules,
    currentConfig,
    generationBatches,
    configVersions,
    loading,
    setLoading,
    hasQueried,
    setHasQueried,
    recordsReloadVersion,
    monthPlan,
    loadStudentConfig,
    loadRecords,
  }
}
